






























import { Component, Provide, Vue } from 'vue-property-decorator';
import { MetaInfo } from 'vue-meta/types/vue-meta';
import MainButton from '@/main-components/common/MainButton.vue';
import { getToken } from '@/utils/cookie';
import CollectionItemStore, {
  CollectionItemStoreInterface,
} from '@/resource/collection-item/collection-item.store';
import PaperHeader from '@/main-components/paper/PaperHeader.vue';
import { OnSubmitReturn } from '@/main-components/common/Form.vue';
import { UpdatePaper } from '@/main-components/common/modal/ModalEditPaper.vue';
import PaperContent from '@/main-components/paper/PaperContent.vue';
import NotFound from '@/components/common/NotFound.vue';
import CollectionStore, {
  CollectionStoreInterface,
} from '@/resource/collection/collection.store';
import { QuestionLanguage } from '@/resource/subject/subject.model';
import seoDefault from '@/utils/seo-default';

@Component({
  components: {
    MainButton,
    PaperHeader,
    PaperContent,
    NotFound,
  },
  metaInfo(this: Paper): MetaInfo {
    return {
      title: 'Latihan |',
      meta: [
        ...seoDefault({
          title: 'Latihan | Mysoalan',
          description: 'Latihan',
        }),
      ],
    };
  },
})
export default class Paper extends Vue {
  paperItemStore: CollectionItemStoreInterface = CollectionItemStore;
  paperStore: CollectionStoreInterface = CollectionStore;

  isPaperLoading = true;
  isPaperItemsLoading = true;
  isMorePaperItemsLoading = false;

  showRearrange = false;
  isQuestionPositionChanged = false;

  get paperUuid() {
    const params = this.$route.params.uuid.split('-');
    return params[params.length - 1] ? params[params.length - 1] : 'not-avail';
  }

  get paper() {
    return this.paperItemStore.collection;
  }

  get paperItems() {
    return this.paperItemStore.collectionItems;
  }

  get totalPaperItems() {
    return this.paperItemStore.collectionItems.length;
  }

  get isAllPaperItemsLoaded() {
    return this.totalPaperItems === this.paperItemStore.pagination.totalRecords;
  }

  get isLoggedIn() {
    return getToken() ? true : false;
  }

  get isLoading() {
    return (
      this.isPaperLoading ||
      this.isPaperItemsLoading ||
      this.isMorePaperItemsLoading
    );
  }

  async created() {
    if (this.paperUuid) {
      await this.getPaper();
      await this.fetchPaperItems();
    }
  }

  beforeDestroy() {
    this.paperItemStore.resetData();
  }

  async getPaper() {
    this.isPaperLoading = true;

    await this.paperItemStore.fetchCollection(this.paperUuid);

    this.isPaperLoading = false;
  }

  async updatePaper(params: UpdatePaper): Promise<OnSubmitReturn | null> {
    if (this.isLoading) return null;
    this.isPaperLoading = true;

    const bodyRequest = {
      uuid: this.paperUuid,
      name: params.name,
      subjectUuid: (params.subject && params.subject.selectedSubject) || '',
    };

    await this.paperItemStore.updateCollectionDetail(bodyRequest);

    this.isPaperLoading = false;

    if (this.paperItemStore.error) {
      return { error: this.paperItemStore.error };
    }

    return null;
  }

  async fetchPaperItems() {
    this.isPaperItemsLoading = true;

    if (this.paper) {
      this.paperItemStore.pagination.isResetPage = true;
      this.paperItemStore.pagination.page = 1;
      await this.paperItemStore.fetchCollectionItems(this.paperUuid);
    }
    this.isPaperItemsLoading = false;
  }

  async fetchMorePaperItems() {
    if (this.isLoading) return null;

    const { pagination } = this.paperItemStore;

    if (pagination.page >= pagination.totalPages) return;

    this.isMorePaperItemsLoading = true;

    this.paperItemStore.pagination.page += 1;
    await this.paperItemStore.fetchCollectionItems(this.paperUuid);

    this.isMorePaperItemsLoading = false;
  }

  @Provide('addQuestionToPaper')
  async addQuestionToPaper(
    questionId: number,
    answerPosition: number,
    isSubjective: boolean,
    languages: QuestionLanguage[],
  ) {
    if (this.isLoading) return;
    this.isPaperItemsLoading = true;

    if (this.paper) {
      await this.paperStore.addQuestion({
        questionId,
        collectionUuid: this.paper.uuid,
        answerPosition: answerPosition,
        isSubjective: isSubjective,
        languages,
      });

      await this.fetchPaperItems();
    }

    this.isPaperItemsLoading = false;
  }

  @Provide('toggleShowRearrange')
  async toggleShowRearrange(value: boolean) {
    if (!value && this.isQuestionPositionChanged) {
      await this.fetchPaperItems();
    }

    this.showRearrange = value;
    this.isQuestionPositionChanged = false;
  }

  @Provide('generatePdf')
  async generatePdf() {
    if (this.isLoading) return;

    await this.paperItemStore.createPdf();

    if (this.paperItemStore.error) {
      this.$sendMpEvent({ event: this.$mp_value.ErrorGeneratePdf });
    }

    if (this.paperItemStore.pdfFile && this.paper) {
      this.$sendMpEvent({ event: this.$mp_value.PaperGeneratePdf });

      const blob = new Blob([this.paperItemStore.pdfFile], {
        type: 'application/pdf',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.paper.name);
      document.body.appendChild(link);
      link.click();
    }
  }

  @Provide('movePaperItem')
  movePaperItem(fromIndex: number, toIndex: number) {
    const currentItem = this.paperItems[fromIndex];
    const originalItem = this.paperItems[toIndex];

    const currentItemPosition = currentItem.questionPosition;
    const originalItemPosition = originalItem.questionPosition;

    currentItem.questionPosition = originalItemPosition;
    originalItem.questionPosition = currentItemPosition;

    this.paperItems.splice(fromIndex, 1);
    this.paperItems.splice(toIndex, 0, currentItem);
    this.isQuestionPositionChanged = true;
  }

  @Provide('savePaperItemPositions')
  async savePaperItemPositions() {
    if (this.isLoading) return;

    if (this.isQuestionPositionChanged) {
      this.isPaperItemsLoading = true;
      this.showRearrange = false;
      this.isQuestionPositionChanged = false;

      await this.paperItemStore.updateQuestionPositions();
      await this.fetchPaperItems();
    } else {
      this.showRearrange = false;
      this.isQuestionPositionChanged = false;
    }
  }

  @Provide('addOrEditQuestion')
  async addOrEditQuestion(
    isSubjective: boolean,
    paperItemId: number | null = null,
    questionId: number | null = null,
  ) {
    this.$router.push({
      name: 'question-editor',
      query: {
        'is-subjective': isSubjective ? 'true' : 'false',
        'paper-uuid': this.paper?.uuid,
        'paper-item-id': paperItemId ? `${paperItemId}` : '',
        'question-id': questionId ? `${questionId}` : '',
      },
    });
  }
}
